import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { User, UserDocumentData, UserModel, UserRole } from '../models/User';

// Mirrors iOS AuthViewModel — loads user doc from all_users → admins → users
export class AuthService {
  static onAuthStateChanged(callback: (user: User | null, isLoading: boolean) => void): () => void {
    return onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (!firebaseUser || firebaseUser.isAnonymous) {
        if (firebaseUser?.isAnonymous) {
          await firebaseSignOut(auth);
        }
        callback(null, false);
        return;
      }
      const user = await AuthService.loadUserData(firebaseUser.uid);
      callback(user, false);
    });
  }

  static async loadUserData(uid: string): Promise<User | null> {
    // all_users → admins → users (same lookup order as iOS)
    const collections = ['all_users', 'admins', 'users'];
    for (const coll of collections) {
      const snap = await getDoc(doc(db, coll, uid));
      if (snap.exists()) {
        const data = snap.data() as UserDocumentData;
        data.id = snap.id;
        return UserModel.fromFirestore(data);
      }
    }
    return null;
  }

  static async signIn(email: string, password: string): Promise<User> {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const user = await AuthService.loadUserData(cred.user.uid);
    if (!user) {
      throw new Error('Account exists but no user profile was found.');
    }
    return user;
  }

  // Mirrors iOS signUpUser — 7-day trial
  static async signUp(email: string, password: string, displayName: string, fullName?: string): Promise<User> {
    const cred = await createUserWithEmailAndPassword(auth, email, password);

    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + 7);

    const user: User = {
      id: cred.user.uid,
      email,
      displayName,
      fullName,
      role: UserRole.REGULAR,
      createdAt: new Date(),
      isActive: true,
      trialEndDate,
      isTrialActive: true
    };

    const docData = {
      id: user.id,
      email: user.email,
      displayName: user.displayName,
      fullName: user.fullName ?? null,
      phoneNumber: null,
      role: user.role,
      createdAt: user.createdAt,
      isActive: user.isActive,
      trialEndDate: user.trialEndDate,
      isTrialActive: user.isTrialActive,
      subscriptionStatus: null,
      subscriptionType: null,
      subscriptionEndDate: null
    };

    // Role 'user' can only be written to the users + all_users collections;
    // admin docs are provisioned server-side only.
    await setDoc(doc(db, 'users', user.id), docData);
    await setDoc(doc(db, 'all_users', user.id), docData);

    return user;
  }

  static async signOut(): Promise<void> {
    await firebaseSignOut(auth);
  }
}
